import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import {
  useCreateSowing,
  useRecordGerminationWave,
  useRejectInstruction,
  useAmendInstruction,
  useDecideAmendment,
  useAvailableGreenhouses,
  useAvailableBays,
  useAvailableBeds,
  useEligibleRequisitions,
} from "../../hooks/useSowingLifecycle";
import type { SowingDto } from "../../services/sowingLifecycleApi";

// ─── Create Sowing Dialog (creates Sowing child of SowingInstruction) ─────────

interface CreateSowingDialogProps {
  instruction: { id: string; variety: string; quantityToSow: number };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const CreateSowingDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: CreateSowingDialogProps) => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    sowingDate: today,
    traysSown: "",
    requisitionId: "",
  });

  const createSowingMutation = useCreateSowing();
  const confirm = useConfirm();
  const requisitionsQuery = useEligibleRequisitions(instruction.id);
  const notify = useNotification();

  const handleSubmit = () => {
    if (!formData.sowingDate || !formData.traysSown) {
      notify.error("Sowing date and trays sown are required");
      return;
    }

    const traysSown = parseFloat(formData.traysSown);
    if (traysSown <= 0) {
      notify.error("Trays sown must be greater than 0");
      return;
    }

    confirm({
      title: "Record sowing?",
      description: "The sown trays will be recorded for this instruction.",
      confirmLabel: "Record sowing",
      body: SowingConfirmBody(instruction, [
        { label: "Sowing date", value: formData.sowingDate },
        { label: "Trays sown", value: `${traysSown}` },
      ]),
      onConfirm: () =>
        createSowingMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            sowingDate: new Date(formData.sowingDate).toISOString(),
            traysSown,
            requisitionId: formData.requisitionId || undefined,
          })
          .then(() => {
            onOpenChange(false);
            notify.success("Sowing recorded successfully");
            onSuccess?.();
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error) || "Failed to create sowing");
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Sowing</DialogTitle>
          <DialogDescription>
            {instruction.variety} — Target: {instruction.quantityToSow} trays
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Sowing Date <span className="text-destructive">*</span></FieldLabel>
            <DatePicker
              max={today}
              value={formData.sowingDate}
              onChange={(value) => setFormData((prev) => ({ ...prev, sowingDate: value }))}
              disabled={createSowingMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Trays Sown <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.traysSown}
              onChange={(e) => setFormData((prev) => ({ ...prev, traysSown: e.target.value }))}
              disabled={createSowingMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Requisition <span className="text-muted-foreground text-xs">(links material issue)</span></FieldLabel>
            <Select
              value={formData.requisitionId}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, requisitionId: v }))}
              disabled={createSowingMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select requisition (optional)" />
              </SelectTrigger>
              <SelectContent>
                {requisitionsQuery.data?.map((req) => (
                  <SelectItem key={req.id} value={req.id}>
                    {req.requisitionNumber} — {req.purpose}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={createSowingMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createSowingMutation.isPending}>
              {createSowingMutation.isPending ? "Recording..." : "Record Sowing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Record Germination Wave Dialog (with cascading bed picker) ───────────────

interface RecordGerminationWaveDialogProps {
  sowing: SowingDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const RecordGerminationWaveDialog = ({
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: RecordGerminationWaveDialogProps) => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    germinationDate: today,
    traysGerminated: "",
    greenhouseId: "",
    bayId: "",
    bedAllocations: new Map<string, number>(),
    inlineDiscardTrays: "",
    inlineDiscardReason: "",
  });

  const germinationMutation = useRecordGerminationWave();
  const confirm = useConfirm();
  const greenhousesQuery = useAvailableGreenhouses();
  const baysQuery = useAvailableBays(formData.greenhouseId);
  const bedsQuery = useAvailableBeds(formData.bayId);
  const notify = useNotification();

  const traysGerminatedNum = parseFloat(formData.traysGerminated) || 0;
  const allocatedTotal = Array.from(formData.bedAllocations.values()).reduce((a, b) => a + b, 0);
  const hasZeroValues = Array.from(formData.bedAllocations.values()).some((c) => c <= 0);
  const allocationsValid =
    formData.bedAllocations.size > 0 &&
    !hasZeroValues &&
    allocatedTotal === Math.floor(traysGerminatedNum) &&
    traysGerminatedNum > 0;

  const handleBedToggle = (bedId: string) => {
    setFormData((prev) => {
      const next = new Map(prev.bedAllocations);
      if (next.has(bedId)) {
        next.delete(bedId);
      } else {
        next.set(bedId, 0);
      }
      return { ...prev, bedAllocations: next };
    });
  };

  const handleBedTrayCountChange = (bedId: string, value: string) => {
    const n = Math.max(0, parseInt(value || "0", 10) || 0);
    setFormData((prev) => {
      const next = new Map(prev.bedAllocations);
      next.set(bedId, n);
      return { ...prev, bedAllocations: next };
    });
  };

  const handleSubmit = () => {
    if (!formData.germinationDate || !formData.traysGerminated) {
      notify.error("Date and trays germinated are required");
      return;
    }

    const trays = parseFloat(formData.traysGerminated);
    if (trays <= 0 || trays > sowing.traysRemaining) {
      notify.error(`Trays germinated must be between 0 and ${sowing.traysRemaining}`);
      return;
    }

    if (formData.bedAllocations.size === 0) {
      notify.error("Please assign trays to at least one bed");
      return;
    }
    if (allocatedTotal !== Math.floor(trays)) {
      notify.error(`Per-bed trays must total ${Math.floor(trays)} (currently ${allocatedTotal})`);
      return;
    }
    if (Array.from(formData.bedAllocations.values()).some((c) => c <= 0)) {
      notify.error("Each selected bed must have a tray count greater than zero");
      return;
    }

    const discardTrays = formData.inlineDiscardTrays ? parseFloat(formData.inlineDiscardTrays) : undefined;
    if (discardTrays && discardTrays > 0 && !formData.inlineDiscardReason) {
      notify.error("Discard reason is required when discarding trays");
      return;
    }

    confirm({
      title: "Record germination?",
      description: "Germination will be recorded for this sowing.",
      body: (
        <div className="space-y-0.5">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Germination date</span>
            <span className="font-medium">{formData.germinationDate}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Trays germinated</span>
            <span className="font-medium">{trays}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Beds</span>
            <span className="font-medium">{formData.bedAllocations.size}</span>
          </p>
          {discardTrays != null && discardTrays > 0 && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Discarding</span>
              <span className="font-medium">{discardTrays}</span>
            </p>
          )}
        </div>
      ),
      confirmLabel: "Record germination",
      onConfirm: () =>
        germinationMutation
          .mutateAsync({
            sowingId: sowing.id,
            germinationDate: new Date(formData.germinationDate).toISOString(),
            traysGerminated: trays,
            bedAllocations: Array.from(formData.bedAllocations.entries()).map(([bedId, trayCount]) => ({
              bedId,
              trayCount,
            })),
            inlineDiscardTrays: discardTrays,
            inlineDiscardReason: formData.inlineDiscardReason || undefined,
          })
          .then(() => {
            onOpenChange(false);
            notify.success("Germination wave recorded");
            onSuccess?.();
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error) || "Failed to record germination");
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Germination Wave</DialogTitle>
          <DialogDescription>
            Sowing: {sowing.traysSown} trays — Remaining: {sowing.traysRemaining} trays
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Germination Date <span className="text-destructive">*</span></FieldLabel>
            <DatePicker
              max={today}
              value={formData.germinationDate}
              onChange={(value) => setFormData((prev) => ({ ...prev, germinationDate: value }))}
              disabled={germinationMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Trays Germinated <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={sowing.traysRemaining}
              placeholder={`Max: ${sowing.traysRemaining}`}
              value={formData.traysGerminated}
              onChange={(e) => setFormData((prev) => ({ ...prev, traysGerminated: e.target.value }))}
              disabled={germinationMutation.isPending}
            />
          </Field>

          {/* Cascading bed selection: Greenhouse → Bay → Beds */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold mb-3">Bed Assignment <span className="text-destructive">*</span></p>
            <div className="space-y-3">
              <Field>
                <FieldLabel>Greenhouse</FieldLabel>
                <Select
                  value={formData.greenhouseId}
                  onValueChange={(v) =>
                    setFormData((prev) => ({
                      ...prev,
                      greenhouseId: v,
                      bayId: "",
                      bedAllocations: new Map<string, number>(),
                    }))
                  }
                  disabled={germinationMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select greenhouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {greenhousesQuery.data?.map((gh) => (
                      <SelectItem key={gh.id} value={gh.id}>
                        {gh.greenhouseName} ({gh.availableBedCount} beds)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {formData.greenhouseId && (
                <Field>
                  <FieldLabel>Bay</FieldLabel>
                  <Select
                    value={formData.bayId}
                    onValueChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        bayId: v,
                        selectedBedIds: new Set<string>(),
                      }))
                    }
                    disabled={germinationMutation.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bay" />
                    </SelectTrigger>
                    <SelectContent>
                      {baysQuery.data?.map((bay) => (
                        <SelectItem key={bay.id} value={bay.id}>
                          {bay.bayName} ({bay.availableBedCount} beds)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {formData.bayId && (
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                  {bedsQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading beds...</p>
                  ) : bedsQuery.data?.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No available beds</p>
                  ) : (
                    bedsQuery.data?.map((bed) => {
                      const isSelected = formData.bedAllocations.has(bed.id);
                      const allocation = formData.bedAllocations.get(bed.id) ?? 0;
                      return (
                        <div key={bed.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`bed-${bed.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleBedToggle(bed.id)}
                            disabled={germinationMutation.isPending}
                          />
                          <label htmlFor={`bed-${bed.id}`} className="text-sm cursor-pointer flex-1">
                            {bed.bedNumber}
                            {bed.currentOccupancy > 0 && (
                              <span className="ml-1 text-xs text-muted-foreground">
                                {bed.currentOccupancy} trays on bed
                              </span>
                            )}
                          </label>
                          {isSelected && (
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="trays"
                              value={allocation || ""}
                              onChange={(e) => handleBedTrayCountChange(bed.id, e.target.value)}
                              disabled={germinationMutation.isPending}
                              className="h-7 w-20 text-sm"
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {formData.bedAllocations.size > 0 && (() => {
                const target = Math.floor(traysGerminatedNum) || 0;
                const diff = allocatedTotal - target;
                let message = `Allocated ${allocatedTotal} / ${target} trays`;
                if (target > 0 && diff > 0) message += ` — over by ${diff}, reduce a bed's count`;
                else if (target > 0 && diff < 0) message += ` — need ${-diff} more tray${-diff === 1 ? "" : "s"}`;
                return (
                  <p className={`text-xs ${allocationsValid ? "text-muted-foreground" : "text-destructive"}`}>
                    {message}
                  </p>
                );
              })()}
            </div>
          </div>

          {/* Inline discard (optional) */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold mb-3">Inline Discard <span className="text-muted-foreground text-xs">(optional)</span></p>
            <div className="space-y-3">
              <Field>
                <FieldLabel>Trays Failed</FieldLabel>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.inlineDiscardTrays}
                  onChange={(e) => setFormData((prev) => ({ ...prev, inlineDiscardTrays: e.target.value }))}
                  disabled={germinationMutation.isPending}
                />
              </Field>

              {formData.inlineDiscardTrays && parseFloat(formData.inlineDiscardTrays) > 0 && (
                <Field>
                  <FieldLabel>Discard Reason <span className="text-destructive">*</span></FieldLabel>
                  <Textarea
                    value={formData.inlineDiscardReason}
                    onChange={(e) => setFormData((prev) => ({ ...prev, inlineDiscardReason: e.target.value }))}
                    placeholder="Required reason for discard"
                    disabled={germinationMutation.isPending}
                  />
                </Field>
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={germinationMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={germinationMutation.isPending || !allocationsValid}>
              {germinationMutation.isPending ? "Recording..." : "Record Germination"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Reject Instruction Dialog ────────────────────────────────────────────────

interface RejectInstructionDialogProps {
  instruction: { id: string; variety: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const RejectInstructionDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: RejectInstructionDialogProps) => {
  const [reason, setReason] = useState("");
  const rejectMutation = useRejectInstruction();
  const notify = useNotification();

  const handleSubmit = () => {
    if (!reason.trim()) {
      notify.error("Rejection reason is required");
      return;
    }

    rejectMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          setReason("");
          onOpenChange(false);
          notify.success("Instruction rejected");
          onSuccess?.();
        },
        onError: (error: unknown) => {
          notify.error(getErrorMessage(error) || "Failed to reject instruction");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Sowing Instruction</DialogTitle>
          <DialogDescription>{instruction.variety}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Reason <span className="text-destructive">*</span></FieldLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this instruction cannot be fulfilled..."
              disabled={rejectMutation.isPending}
              rows={4}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={rejectMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSubmit} disabled={rejectMutation.isPending}>
              {rejectMutation.isPending ? "Rejecting..." : "Reject"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Amend Instruction Dialog ─────────────────────────────────────────────────

interface AmendInstructionDialogProps {
  instruction: {
    id: string;
    variety: string;
    quantityToSow: number;
    scheduledSowingDate: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const AmendInstructionDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: AmendInstructionDialogProps) => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    newQuantityToSow: instruction.quantityToSow.toString(),
    newScheduledStartDate: instruction.scheduledSowingDate?.split("T")[0] || today,
    newScheduledEndDate: "",
    reason: "",
  });

  const amendMutation = useAmendInstruction();
  const notify = useNotification();

  const handleSubmit = () => {
    if (!formData.reason.trim()) {
      notify.error("Amendment reason is required");
      return;
    }

    if (!formData.newScheduledStartDate || !formData.newScheduledEndDate) {
      notify.error("Both start and end dates are required");
      return;
    }

    if (formData.newScheduledEndDate < formData.newScheduledStartDate) {
      notify.error("End date must be on or after start date");
      return;
    }

    amendMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        newQuantityToSow: parseFloat(formData.newQuantityToSow),
        newScheduledStartDate: new Date(formData.newScheduledStartDate).toISOString(),
        newScheduledEndDate: new Date(formData.newScheduledEndDate).toISOString(),
        reason: formData.reason.trim(),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          notify.success("Amendment submitted for approval");
          onSuccess?.();
        },
        onError: (error: unknown) => {
          notify.error(getErrorMessage(error) || "Failed to submit amendment");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Propose Amendment</DialogTitle>
          <DialogDescription>
            {instruction.variety} — Current: {instruction.quantityToSow} trays
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>New Quantity <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.newQuantityToSow}
              onChange={(e) => setFormData((prev) => ({ ...prev, newQuantityToSow: e.target.value }))}
              disabled={amendMutation.isPending}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel>Start Date <span className="text-destructive">*</span></FieldLabel>
              <DatePicker
                min={today}
                value={formData.newScheduledStartDate}
                onChange={(value) => setFormData((prev) => ({ ...prev, newScheduledStartDate: value }))}
                disabled={amendMutation.isPending}
              />
            </Field>
            <Field>
              <FieldLabel>End Date <span className="text-destructive">*</span></FieldLabel>
              <DatePicker
                min={formData.newScheduledStartDate || today}
                value={formData.newScheduledEndDate}
                onChange={(value) => setFormData((prev) => ({ ...prev, newScheduledEndDate: value }))}
                disabled={amendMutation.isPending}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel>Reason <span className="text-destructive">*</span></FieldLabel>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Explain why this amendment is needed..."
              disabled={amendMutation.isPending}
              rows={3}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={amendMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={amendMutation.isPending}>
              {amendMutation.isPending ? "Submitting..." : "Submit Amendment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Decide Amendment Dialog (Approve/Reject) ─────────────────────────────────

interface DecideAmendmentDialogProps {
  amendment: {
    id: string;
    instructionNumber?: string;
    newQuantityToSow: number;
    newScheduledStartDate: string;
    newScheduledEndDate: string;
    reason: string;
    amendedByUserName?: string;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const DecideAmendmentDialog = ({
  amendment,
  open,
  onOpenChange,
  onSuccess,
}: DecideAmendmentDialogProps) => {
  const [comment, setComment] = useState("");
  const decideMutation = useDecideAmendment();
  const notify = useNotification();

  const handleDecision = (approve: boolean) => {
    decideMutation.mutate(
      {
        amendmentId: amendment.id,
        approve,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: () => {
          setComment("");
          onOpenChange(false);
          notify.success(approve ? "Amendment approved" : "Amendment rejected");
          onSuccess?.();
        },
        onError: (error: unknown) => {
          notify.error(getErrorMessage(error) || "Failed to process decision");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Amendment Decision</DialogTitle>
          <DialogDescription>
            {amendment.instructionNumber} — Proposed by {amendment.amendedByUserName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/50 p-3 text-sm space-y-1">
            <p><strong>Qty:</strong> {amendment.newQuantityToSow} trays</p>
            <p><strong>Start:</strong> {new Date(amendment.newScheduledStartDate).toLocaleDateString()}</p>
            <p><strong>End:</strong> {new Date(amendment.newScheduledEndDate).toLocaleDateString()}</p>
            <p><strong>Reason:</strong> {amendment.reason}</p>
          </div>

          <Field>
            <FieldLabel>Comment <span className="text-muted-foreground text-xs">(optional)</span></FieldLabel>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              disabled={decideMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={decideMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDecision(false)} disabled={decideMutation.isPending}>
              Reject
            </Button>
            <Button onClick={() => handleDecision(true)} disabled={decideMutation.isPending}>
              Approve
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
